import React, { useCallback, useEffect, useId, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import ErrorDisplay from "../common/ErrorDisplay";
import { Problem } from "../api/backendApi";
import {
  vehicleFileAPI,
  VehicleFileSearchResultModel,
} from "../api/vehicleFileAPI";
import { Search } from "react-bootstrap-icons";
import { drawingApi } from "../api/drawingApi";

interface ImportDrawingModalProps {
  vehicleFileNumber: string;
  onClose: () => void;
  onImport: () => void;
}

function ImportDrawingModal({
  vehicleFileNumber,
  onClose,
  onImport,
}: ImportDrawingModalProps) {
  const [error, setError] = useState<Problem | string | undefined>();
  const [loading, setLoading] = useState(false);
  const [vehicleFiles, setVehicleFiles] = useState<
    VehicleFileSearchResultModel[]
  >([]);
  const [selectedValue, setSelectedValue] = useState<string>();
  const [filter, setFilter] = useState("");
  const searchId = useId();
  const [confirming, setConfirming] = useState(false);

  const refresh = useCallback(
    async (selectedValue: string | undefined, filter: string) => {
      setLoading(true);

      const trimmedFilter = filter.trim();
      if (trimmedFilter === "") {
        setVehicleFiles([]);
        setSelectedValue(undefined);
      } else {
        // load vehicles
        const result = await vehicleFileAPI.searchVehicleFiles(
          trimmedFilter,
          true,
          20,
        );

        if (result.ok) {
          setVehicleFiles(result.value);
          setError(undefined);

          if (selectedValue) {
            if (!result.value.find((vf) => vf.number === selectedValue)) {
              setSelectedValue(undefined);
            }
          }
        } else {
          setError(result.problem);
          setSelectedValue(undefined);
        }
      }

      setLoading(false);
    },
    [],
  );

  useEffect(() => {
    refresh(selectedValue, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const handleImport = async () => {
    if (!selectedValue) return;
    const result = await drawingApi.importMpfz(
      vehicleFileNumber,
      selectedValue,
      undefined,
    );

    if (!result.ok) {
      setError(result.problem);
      setConfirming(false);
      return;
    }

    setError(undefined);
    setSelectedValue(undefined);
    onImport();
  };

  return (
    <Modal
      show={true}
      onHide={() => {
        onClose();
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Import drawing from another file</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {confirming && (
          <div
            className="my-2"
            style={{
              position: "absolute",
              top: 0,
              bottom: 0,
              backgroundColor: "white",
            }}
          >
            <h3>Are you sure?</h3>
            <p>
              This will erase the contents of the current drawing and overwrite
              it with the drawing from '{selectedValue}'!
            </p>
          </div>
        )}
        <ErrorDisplay error={error} />
        <Row className="mb-2">
          <Form.Label column htmlFor={searchId} md="auto">
            Search term:
          </Form.Label>
          <Col>
            <Form.Control
              disabled={loading}
              className="w-100"
              id={searchId}
              defaultValue=""
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={(e) =>
                e.code === "Enter" && refresh(selectedValue, filter)
              }
            />
          </Col>
          <Col md="auto">
            <Button
              onClick={() => refresh(selectedValue, filter)}
              disabled={loading}
            >
              {loading ? (
                <Spinner size="sm" />
              ) : (
                <Search style={{ verticalAlign: "-0.125em" }} />
              )}
            </Button>
          </Col>
        </Row>
        <div style={{ height: 400, overflowY: "auto" }}>
          <Table>
            <tbody>
              {vehicleFiles.length > 0 ? (
                vehicleFiles.map((vf) => (
                  <tr
                    key={vf.number}
                    role="button"
                    onClick={() => setSelectedValue(vf.number)}
                    className={
                      "mb-2" +
                      (selectedValue === vf.number ? " table-active" : "")
                    }
                  >
                    <td>
                      {vf.number}
                      {vf.number === vehicleFileNumber &&
                        " (current vehicle file)"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="mb-2">
                  <td colSpan={1}>
                    No vehicle files found using the provided search term.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        {confirming && (
          <>
            <Button
              variant="danger"
              size="sm"
              onClick={() => handleImport()}
              style={{ marginRight: "auto" }}
              disabled={loading}
            >
              Confirm Import
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
          </>
        )}
        {!confirming && (
          <>
            <Button
              variant="primary"
              size="sm"
              disabled={loading || !selectedValue}
              onClick={() => {
                setConfirming(true);
                setError(undefined);
              }}
            >
              Import
            </Button>
            <Button variant="secondary" size="sm" onClick={() => onClose()}>
              Cancel
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ImportDrawingModal;
